// Część 1 hero 47-A „parabola fanbase” (paczka H47): H1, podtytuł, grafika, akcja „Zacznij za darmo” z mikrotekstem.
//
// Grafika to ten sam markup, który `rysuj()` z `47a-swiat.js` pisał do DOM, jako JSX z danych generatora
// (`landingParabola.data.ts`): jeden <svg> ramion z gradientem, jeden <svg> na łuk kont (`data-rz`), pigułka
// utworu u nasady, węzły DOM paczek (stos trzech kadrów), podpisów liczby editów i kont (awatar, pierścień barwy
// encji, logo platformy), a na końcu grupa odnowy (łuk ukryty w spoczynku, ożywa w pętli). Świat liczy build,
// nie przeglądarka; ruch dokłada `useHeroMotion` (WAAPI, bez zmian w DOM Reacta).
//
// Dla czytnika grafika jest JEDNYM obrazem (`role="img"` + `landing.hero.parabolaAlt`), wnętrze `aria-hidden`.
// Pole ma orientację `v` / `w` / `h` z `useHeroMedia`; zmiana orientacji montuje pole od nowa (`key`).
// Statyczny HTML (snapshot prerendera) niesie wszystkie trzy pola z `mp-hp-snap` (`scripts/prerender.mjs`).

import { useLayoutEffect, useRef } from 'react'
import type { CSSProperties, Ref } from 'react'
import { Link } from 'react-router'
import { useI18n } from '../../lib/i18n'
import { editWord } from '../../locales/plural'
import { PlatformLogo } from '../../ds/media/PlatformLogo'
import type { PlatformLogoName } from '../../ds/media/PlatformLogo'
import { FALA, TRACK_TIME, logoPx } from '../../lib/landingParabola'
import type { KontoDane, Orient, PaczkaDane, ParabolaData, PlatformKey } from '../../lib/landingParabola'
import { WORLDS } from '../../lib/landingParabola.data'
import { useHeroMotion } from '../../hooks/useHeroMotion'
import '../../styles/landing-parabola.css'

const LOGO: Record<PlatformKey, PlatformLogoName> = { tt: 'tiktok', ig: 'instagram', yt: 'youtube' }
/** Kadr sceny albo stylu editu z `frontend/public/kadry`. */
export const kadrSrc = (k: string) => `/kadry/${k}.jpg`

/** Pigułka utworu: nazwa, fala, czas. Pierścień pulsu (`mp-hp-ut-p`) jest pusty i ukryty w spoczynku. */
export function TrackPill({ name, bars }: { name: string; bars: number[] }) {
  return (
    <div className="mp-hp-ut">
      <span className="mp-hp-ut-p" />
      <b>{name}</b>
      <span className="mp-hp-fala">
        {bars.map((h, i) => <i key={i} style={{ '--h': h } as CSSProperties} />)}
      </span>
      <span className="mp-hp-tm">{TRACK_TIME}</span>
    </div>
  )
}

/**
 * Konto: awatar (miniatura 96 / 192 px z `kadry/av`), pierścień barwy encji (reguła 52) i logo platformy w prawym
 * dolnym rogu od średnicy 18 px (reguła 53). `at` = środek w px pola grafiki; bez niego konto stoi w układzie.
 */
export function Account({ id, e, d, pf, av, at, data }: {
  id?: number; e: 1 | 2 | 3; d: number; pf: PlatformKey; av: string; at?: { x: number; y: number }; data?: Record<string, string | number>
}) {
  const lg = logoPx(d)
  const style = { '--d': `${d}px`, ...(at ? { left: at.x, top: at.y } : {}) } as CSSProperties
  return (
    <span className={`mp-hp-ak e${e}`} data-k={id} {...data} style={style}>
      <span className="mp-hp-ak-r" />
      <span className="mp-hp-ak-in">
        <img src={`/kadry/av/${av}.jpg`} srcSet={`/kadry/av/${av}@2x.jpg 2x`} alt="" width={Math.round(d)} height={Math.round(d)} decoding="async" />
        {lg ? <span className="mp-hp-lg" style={{ '--l': `${lg}px` } as CSSProperties}><PlatformLogo name={LOGO[pf]} /></span> : null}
      </span>
    </span>
  )
}

function Pack({ p }: { p: PaczkaDane }) {
  return (
    <div className="mp-hp-pk" data-p={p.id} data-rz={p.i} style={{ left: p.x, top: p.y, '--fw': `${p.fw}px`, '--fh': `${p.fh}px` } as CSSProperties}>
      <span className="mp-hp-pk-in">
        <b className="mp-hp-pk-g" />
        {p.kadry.map((k, i) => <i key={i} className={`f${i}`} style={{ backgroundImage: `url(${kadrSrc(`styl-${k}`)})` }} />)}
      </span>
    </div>
  )
}

const acct = (k: KontoDane) => (
  <Account key={k.id} id={k.id} e={k.e} d={k.d} pf={k.pf} av={k.av} at={k} data={{ 'data-p': k.p, 'data-rz': k.rz }} />
)

/** Pole grafiki jednej orientacji w klatce spoczynku (to samo w statycznym HTML i po montażu). */
export function ParabolaField({ world: W, snap = false, fieldRef }: { world: ParabolaData; snap?: boolean; fieldRef?: Ref<HTMLDivElement> }) {
  const { t, locale } = useI18n()
  const gid = `mp-hp-gr-${W.o}`
  const box = { viewBox: `0 0 ${W.W} ${W.H}`, width: W.W, height: W.H, 'aria-hidden': true } as const
  const stale = W.paczki.filter((p) => !p.ost), ost = W.paczki.filter((p) => p.ost)
  const n = W.nasada
  return (
    <div ref={fieldRef} className={`mp-hp-g is-${W.o}${snap ? ' mp-hp-snap' : ''}`} style={{ width: W.W, height: W.H }}>
      <svg className="mp-hp-sv mp-hp-ramiona" {...box}>
        <defs>
          <linearGradient id={gid} gradientUnits="userSpaceOnUse" x1={W.gx[0]} y1={W.gx[1]} x2={W.gx[2]} y2={W.gx[3]}>
            <stop offset="0" style={{ stopColor: 'var(--accent-light)' }} />
            <stop offset=".85" style={{ stopColor: 'var(--pink)' }} />
          </linearGradient>
        </defs>
        {W.ramiona.flatMap((r) => [
          ...r.ram.map((d, i) => <path key={`r${r.s}-${i}`} className="mp-hp-ram" stroke={`url(#${gid})`} d={d} />),
          ...r.ogon.map((d, i) => <path key={`o${r.s}-${i}`} className="mp-hp-ogon" d={d} />),
        ])}
      </svg>
      {W.luki.map((l, k) => (
        <svg key={k} className="mp-hp-sv mp-hp-rz" data-rz={k} {...box}>
          {l.map((d, i) => <path key={i} className="mp-hp-wl" d={d} />)}
        </svg>
      ))}
      <div className="mp-hp-nas" style={{ left: n.x, top: n.y, minWidth: n.w, height: n.h }}>
        <TrackPill name={t('landing.hero.map.track')} bars={FALA} />
      </div>
      {stale.flatMap((p) => [
        <Pack key={`p${p.id}`} p={p} />,
        p.lab ? (
          <span key={`n${p.id}`} className="mp-hp-pn" data-p={p.id} data-ax={p.lab.ax} data-ay={p.lab.ay} data-kmin={p.lab.kMin ?? undefined}
                style={{ left: p.lab.x, top: p.lab.y, fontSize: p.lab.fs }}>
            {/* wewnętrzny span: kompensacja skali pola v rośnie od strony paczki (CSS), budowa rusza zewnętrznym */}
            <span>{t('landing.hero.map.series', { n: p.ile, edits: editWord(locale, p.ile) })}</span>
          </span>
        ) : null,
      ])}
      {W.konta.filter((k) => !k.ost).map(acct)}
      {/* Fala odnowy: skrajny łuk (dwie paczki, konta, linia) ukryty w spoczynku, żyje w pętli. */}
      <div className="mp-hp-fn">
        <svg className="mp-hp-sv mp-hp-fn-l" {...box}>
          {W.lukOdnowy.map((d, i) => <path key={i} className="mp-hp-wl" d={d} />)}
        </svg>
        {ost.map((p) => <Pack key={p.id} p={p} />)}
        {W.konta.filter((k) => k.ost).map(acct)}
      </div>
    </div>
  )
}

export interface HeroParabolaProps {
  orient: Orient
  /** Ruch dozwolony (bez `prefers-reduced-motion`): salwa, budowa, pętla, paralaksa. */
  motion: boolean
  /** Przycisk hero: z niego strona liczy próg dolnego paska akcji i akcji nagłówka. */
  ctaRef?: Ref<HTMLAnchorElement>
  micro: string
}

export default function HeroParabola({ orient, motion, ctaRef, micro }: HeroParabolaProps) {
  const { t } = useI18n()
  const field = useRef<HTMLDivElement>(null)
  const par = useRef<HTMLDivElement>(null)
  const gw = useRef<HTMLDivElement>(null)
  const world = WORLDS[orient]
  useHeroMotion(field, par, gw, world, motion)
  // Statyczny HTML niesie założoną rezerwę paska cookie (`h47-snap`, prerender); od montażu mówi ją sam pasek.
  useLayoutEffect(() => { document.documentElement.classList.remove('h47-snap') }, [])
  return (
    <section className="mp-hp" aria-labelledby="landing-hero-heading">
      <div className="mp-hp-tx">
        <h1 id="landing-hero-heading">{t('landing.hero.fanbaseTitle')}</h1>
        <p className="mp-hp-sub">{t('landing.hero.fanbaseSub')}</p>
      </div>
      <div className="mp-hp-gw" ref={gw} role="img" aria-label={t('landing.hero.parabolaAlt')}>
        <div className="mp-hp-par" ref={par} aria-hidden="true">
          <ParabolaField key={orient} world={world} fieldRef={field} />
        </div>
      </div>
      <div className="mp-hp-cta">
        <Link to="/register" className="btn btn-primary" data-cta="scena" ref={ctaRef}>{t('landing.hero.ctaStart')}</Link>
        <small>{micro}</small>
      </div>
    </section>
  )
}
